import { DefaultDisabledBenefitValues } from "@platform/openapi-types";

import { BenefitDefinition } from "../../../benefits/benefit.types";
import { CellSpec } from "../../shared/cellSpecs";

import {
  applyCellEnumValues,
  createTypeDefinition,
  createValueDefinition,
} from "./builderUtils";

export interface TypeDurationBenefitPairConfig {
  prefix: string;
  typeLabel: string;
  typeCell: CellSpec;
  typeEnumValues?: string[];
  durationLabel?: string;
  durationCell: CellSpec;
  typeDescription: string;
  durationDescription: string;
  disabledValues?: string[];
  nested?: boolean;
  networkDependent?: boolean;
  typeDefaultValue?: unknown;
  durationDefaultValue?: unknown;
  durationCellVariants?: {
    dependsOn?: string;
    options: Record<string, CellSpec>;
    default: CellSpec;
  };
  isRequired?: boolean;
}

/**
 * Builds a type/duration benefit definition pair.
 *
 * Creates two related benefit definitions:
 * - `{prefix}Type`: The type selection field (e.g., "days", "months")
 * - `{prefix}Duration`: The duration value field (conditionally editable)
 */
export function buildTypeDurationBenefitPair<TPrefix extends string>(
  config: TypeDurationBenefitPairConfig & { prefix: TPrefix },
): {
  [K in `${TPrefix}Type` | `${TPrefix}Duration`]: BenefitDefinition;
} {
  const {
    prefix,
    typeLabel,
    typeCell,
    typeEnumValues,
    durationLabel = "Duration",
    durationCell,
    typeDescription,
    durationDescription,
    disabledValues,
    nested = true,
    networkDependent = false,
    typeDefaultValue,
    durationDefaultValue,
    durationCellVariants,
    isRequired,
  } = config;

  const finalDisabledValues = disabledValues ?? [
    ...DefaultDisabledBenefitValues,
  ];

  const finalTypeCell = applyCellEnumValues(
    typeCell,
    typeEnumValues,
    typeDefaultValue,
  );

  const typeDef = createTypeDefinition(
    typeLabel,
    typeDescription,
    finalTypeCell,
    networkDependent,
    prefix,
    "Duration",
    typeDefaultValue,
    isRequired,
  );

  const typeDefLabel = `${prefix}Type`;
  const cellVariants = durationCellVariants
    ? {
        dependsOn: durationCellVariants.dependsOn ?? typeDefLabel,
        ...durationCellVariants,
      }
    : undefined;

  const durationDef = createValueDefinition(
    durationLabel,
    durationDescription,
    durationCell,
    networkDependent,
    nested,
    prefix,
    finalDisabledValues,
    durationDefaultValue,
    cellVariants,
  );

  return {
    [typeDefLabel]: typeDef,
    [`${prefix}Duration`]: durationDef,
  } as {
    [K in `${TPrefix}Type` | `${TPrefix}Duration`]: BenefitDefinition;
  };
}

export interface AmountFieldConfig<TSuffix extends string = string> {
  suffix: TSuffix;
  label: string;
  cell: CellSpec;
  description: string;
  defaultValue?: unknown;
  disabledValues?: string[];
}

export interface TypeMultipleAmountsBenefitConfig<
  TPrefix extends string = string,
  TSuffixes extends string = string,
> {
  prefix: TPrefix;
  typeLabel: string;
  typeCell: CellSpec;
  typeEnumValues?: string[];
  typeDescription: string;
  amountFields: AmountFieldConfig<TSuffixes>[];
  disabledValues?: string[];
  nested?: boolean;
  networkDependent?: boolean;
  typeDefaultValue?: unknown;
  isRequired?: boolean;
  isValueRequired?: boolean;
  typeEditableWhen?: BenefitDefinition["editableWhen"];
  /**
   * The MP configuration field that shows or hides this row. Set on the type
   * definition, and on the amount fields too when they are not nested — a
   * nested field renders with its parent and goes when the parent goes, but a
   * top-level one has to carry the key itself.
   */
  mpConfigKey?: string;
  /**
   * Keeps the rows off the Build tab, where no MP configuration is in play.
   * Set on the type definition, and on the amount fields too when they are
   * not nested, same as `mpConfigKey`.
   */
  mpOnly?: boolean;
}

/**
 * Builds a type field with multiple associated amount fields.
 *
 * Common pattern in Vision (copay + allowance) and STD (amount + minimum).
 */
export function buildTypeMultipleAmountsBenefit<
  TPrefix extends string,
  TSuffixes extends string,
>(
  config: TypeMultipleAmountsBenefitConfig<TPrefix, TSuffixes>,
): {
  [K in `${TPrefix}Type` | `${TPrefix}${TSuffixes}`]: BenefitDefinition;
} {
  const {
    prefix,
    typeLabel,
    typeCell,
    typeEnumValues,
    typeDescription,
    amountFields,
    disabledValues,
    nested = true,
    networkDependent = false,
    typeDefaultValue,
    isRequired,
    isValueRequired,
    typeEditableWhen,
    mpConfigKey,
    mpOnly,
  } = config;

  const finalDisabledValues = disabledValues ?? [
    ...DefaultDisabledBenefitValues,
  ];

  const finalTypeCell = applyCellEnumValues(
    typeCell,
    typeEnumValues,
    typeDefaultValue,
  );

  const refreshCells = amountFields.map((field) => `${prefix}${field.suffix}`);
  const typeDef: BenefitDefinition = {
    label: typeLabel,
    description: typeDescription,
    cell: finalTypeCell,
    networkDependent,
    refreshesCells: refreshCells,
  };
  if (typeDefaultValue !== undefined) {
    typeDef.defaultValue = typeDefaultValue;
  }
  if (isRequired) {
    typeDef.isRequired = true;
  }
  if (typeEditableWhen) {
    typeDef.editableWhen = typeEditableWhen;
  }
  if (mpConfigKey) {
    typeDef.mpConfigKey = mpConfigKey;
  }
  if (mpOnly) {
    typeDef.mpOnly = true;
  }

  const result: Record<string, BenefitDefinition> = {
    [`${prefix}Type`]: typeDef,
  };

  for (const amountField of amountFields) {
    const amountDef = createValueDefinition(
      amountField.label,
      amountField.description,
      amountField.cell,
      networkDependent,
      nested,
      prefix,
      amountField.disabledValues ?? finalDisabledValues,
      amountField.defaultValue,
      undefined,
      isValueRequired,
    );
    // A nested amount renders with its type row and disappears with it; a
    // top-level one is its own row, so it needs the key to be hidden too.
    if (mpConfigKey && !nested) {
      amountDef.mpConfigKey = mpConfigKey;
    }
    if (mpOnly && !nested) {
      amountDef.mpOnly = true;
    }
    result[`${prefix}${amountField.suffix}`] = amountDef;
  }

  return result as {
    [K in `${TPrefix}Type` | `${TPrefix}${TSuffixes}`]: BenefitDefinition;
  };
}
